import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../../../../../api';
import EquipmentChecklistModal from './EquiementModal/EquiementModal';

// Component for managing equipment checklists, handling CRUD operations for checklist items
const ManageEquipment = ({ equipmentId, onClose }) => {
  // State to hold the list of checklist items
  const [checklistItems, setChecklistItems] = useState([]); // Always an array
  // State to manage loading status
  const [isLoading, setIsLoading] = useState(false);
  // State for current page in pagination
  const [currentPage, setCurrentPage] = useState(1);
  // Constant for items per page in pagination
  const [itemsPerPage] = useState(10);
  // State to control visibility of edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  // State to control visibility of add modal
  const [showAddModal, setShowAddModal] = useState(false);
  // State to hold the item being edited
  const [currentEditItem, setCurrentEditItem] = useState(null);

  // Helper function to show alerts for success or error messages
  const showAlert = (message, isSuccess = true) => {
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);
  };

  // Function to fetch checklist items from the API
  const fetchChecklistItems = async () => {
    if (!equipmentId) return;

    setIsLoading(true);
    try {
      const response = await axiosInstance.get('ems/equipment-checklist', {
        params: { equipment_id: equipmentId }
      });

      // Safely extract the array from response.data
      let items = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          items = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          items = response.data.data;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          items = response.data.items;
        }
        // Add more fallback paths if needed based on actual API structure
      }

      setChecklistItems(items);
    } catch (error) {
      console.error('API Error:', error);
      showAlert('Failed to load checklist items: ' + (error.response?.data?.message || error.message), false);
      setChecklistItems([]); // Ensure it's an array even on error
    } finally {
      setIsLoading(false);
    }
  };

  // Effect hook to fetch checklist items when equipmentId changes
  useEffect(() => {
    fetchChecklistItems();
  }, [equipmentId]);

  // Function to handle adding a new checklist item
  const handleAddItem = async (formData) => {
    try {
      await axiosInstance.post('ems/equipment-checklist', {
        ...formData,
        criteria: Number(formData.criteria) || 0
      });
      showAlert('Checklist item added successfully!');
      fetchChecklistItems();
      return true;
    } catch (error) {
      showAlert('Failed to add checklist item: ' + (error.response?.data?.message || error.message), false);
      return false;
    }
  };

  // Function to handle updating an existing checklist item
  const handleUpdateItem = async (formData) => {
    try {
      await axiosInstance.put(`ems/equipment-checklist/${formData.id}`, {
        ...formData,
        criteria: Number(formData.criteria) || 0
      });
      showAlert('Checklist item updated successfully!');
      fetchChecklistItems();
      return true;
    } catch (error) {
      showAlert('Failed to update checklist item: ' + (error.response?.data?.message || error.message), false);
      return false;
    }
  };

  // Function to handle deleting a checklist item
  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axiosInstance.delete(`ems/equipment-checklist/${itemId}`);
      showAlert('Item deleted successfully!');
      fetchChecklistItems();
    } catch (error) {
      showAlert('Failed to delete checklist item: ' + (error.response?.data?.message || error.message), false);
    }
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = Array.isArray(checklistItems)
    ? checklistItems.slice(indexOfFirstItem, indexOfLastItem)
    : [];

  const totalPages = Math.ceil(checklistItems.length / itemsPerPage);

  // Function to change the current page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Render the modal structure for managing equipment checklist
  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Manage Equipment Checklist</h5>
            <button className="btn btn-outline-primary ms-auto" onClick={() => setShowAddModal(true)}>
              Add Item
            </button>
          </div>

          <div className="modal-body">
            <div className="table-responsive">
              <table className="table mb-0 align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Criteria</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr key={item.id}>
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{item.name || '—'}</td>
                        <td>{item.criteria !== undefined ? item.criteria : '—'}</td>
                        <td>
                          <button
                            className="h4 me-3 border-0 bg-transparent"
                            onClick={() => {
                              setCurrentEditItem(item);
                              setShowEditModal(true);
                            }}
                          >
                            <i className="ti ti-edit text-warning"></i>
                          </button>
                          <button
                            className="h4 me-3 border-0 bg-transparent"
                            onClick={() => handleDelete(item.id)}
                          >
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        No checklist items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {checklistItems.length > itemsPerPage && (
              <nav className="mt-3">
                <ul className="pagination justify-content-center">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => paginate(i + 1)}>
                        {i + 1}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline-danger" onClick={onClose}>
              Close
            </button>
          </div>

          {/* Add Modal */}
          {showAddModal && (
            <EquipmentChecklistModal
              equipmentId={equipmentId}
              onClose={() => setShowAddModal(false)}
              onSubmit={handleAddItem}
            />
          )}

          {/* Edit Modal */}
          {showEditModal && currentEditItem && (
            <EquipmentChecklistModal
              equipmentId={equipmentId}
              itemId={currentEditItem.id}
              initialData={currentEditItem}
              onClose={() => {
                setShowEditModal(false);
                setCurrentEditItem(null);
              }}
              onSubmit={handleUpdateItem}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageEquipment;